import { useCallback, useEffect, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import type { IRowNode, IStatusPanelParams } from 'ag-grid-enterprise';

import { ifDefined, isNotEmpty } from '@enterprise/core';
import { DateFormat } from '@enterprise/ui';

import type { ReportRow } from './types/report-row.type';

/**
 * Custom AG Grid status panel that displays the most recent `createdDate`
 * found across the grid's row data. Rows are expected to extend `BaseReportType`.
 */
function ReportCreatedDateStatusPanel({ api }: IStatusPanelParams) {
  const [latestDate, setLatestDate] = useState('');

  const recompute = useCallback(() => {
    let maxIso = '';

    api.forEachNode((node: IRowNode<ReportRow>) => {
      ifDefined(node.data?.createdDate, (date) => {
        if (date > maxIso) {
          maxIso = date;
        }
      });
    });

    setLatestDate(maxIso);
  }, [api]);

  useEffect(() => {
    recompute();

    const events = ['firstDataRendered', 'modelUpdated', 'rowDataUpdated'] as const;

    for (const event of events) {
      api.addEventListener(event, recompute);
    }

    return () => {
      for (const event of events) {
        api.removeEventListener(event, recompute);
      }
    };
  }, [api, recompute]);

  const hasDate = isNotEmpty(latestDate);
  const date = hasDate ? new Date(latestDate) : new Date();

  return (
    <Flex
      className="ag-status-name-value"
      lineHeight="initial!">
      {hasDate && (
        <Flex
          className="ag-status-name-value-value"
          direction="column">
          <Flex
            align="center"
            gap={1}>
            <Flex align="center">Generated</Flex>
            <Flex align="center">
              <DateFormat
                format={{
                  isRelative: true,
                }}
                value={date}
              />
            </Flex>
          </Flex>
          <Flex
            align="center"
            fontSize="xs"
            fontWeight="normal">
            <DateFormat
              format="MM/dd/yyyy h:mm a"
              value={date}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export default ReportCreatedDateStatusPanel;
